import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Paper, Box, Button, 
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Alert,
  Grid, Card, CardContent, CardActions, useMediaQuery, useTheme,
  Tooltip, Avatar, Stack, Divider
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as ViewIcon,
  CalendarMonth as CalendarIcon,
  PriorityHigh as PriorityIcon,
  Assignment as TaskIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProjects, createProject, updateProject, deleteProject, Project } from '../api/projectService';
import { createTask, Task } from '../api/taskService';

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Project Dialog State
  const [open, setOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState<Partial<Project>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<number | null>(null);

  // Quick Task Dialog State
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [taskProject, setTaskProject] = useState<Project | null>(null);
  const [newTask, setNewTask] = useState<Partial<Task>>({
    title: '',
    description: '',
    status: 'To Do',
    priority: 'Medium',
    due_date: ''
  });

  const fetchProjectsData = async () => {
    try {
      setLoading(true);
      const data = await getProjects();
      setProjects(data);
    } catch (err: any) {
      setError(`Failed to load projects: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectsData();
  }, []);

  const handleOpenDialog = (project?: Project) => {
    if (project) {
      setCurrentProject({
        ...project,
        start_date: project.start_date ? project.start_date.substring(0, 10) : '',
        target_end_date: project.target_end_date ? project.target_end_date.substring(0, 10) : '',
        actual_end_date: project.actual_end_date ? project.actual_end_date.substring(0, 10) : ''
      });
      setIsEditing(true);
    } else {
      setCurrentProject({
        name: '',
        description: '',
        start_date: '',
        target_end_date: '',
        status: 'Not Started',
        priority: 'Medium',
        budget: undefined,
        owner_id: user?.id || 1
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setCurrentProject({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentProject({ ...currentProject, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      const projectData = {
        ...currentProject,
        budget: currentProject.budget ? parseFloat(currentProject.budget.toString()) : null
      } as Project;
      
      if (isEditing && projectData.id) {
        await updateProject(projectData.id, projectData);
        setSuccess('Project updated successfully');
      } else {
        await createProject(projectData);
        setSuccess('Project created successfully');
      }
      handleCloseDialog();
      fetchProjectsData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(`Failed to save project: ${err.message}`);
    }
  };

  const confirmDelete = async () => {
    if (projectToDelete) {
      try {
        await deleteProject(projectToDelete);
        setSuccess('Project deleted successfully');
        fetchProjectsData();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(`Failed to delete project: ${err.message}`);
      }
    }
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  // Quick Task Handlers
  const handleOpenTaskDialog = (project: Project) => {
    setTaskProject(project);
    setNewTask({
      project_id: project.id,
      title: '',
      description: '',
      status: 'To Do',
      priority: 'Medium',
      due_date: ''
    });
    setOpenTaskDialog(true);
  };

  const handleTaskSubmit = async () => {
    if (!taskProject?.id) return;
    try {
      await createTask(newTask as Task);
      setSuccess(`Task added to ${taskProject.name}`);
      setOpenTaskDialog(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(`Failed to create task: ${err.message}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'primary';
      case 'On Hold': return 'warning';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const canEdit = user && ['admin', 'editor'].includes(user.role);
  const canDelete = user && user.role === 'admin';

  const alpha = (color: string, value: number) => {
    return `${color}${Math.round(value * 255).toString(16).padStart(2, '0')}`;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 800 }}>
            Zandrigo Projects
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track your project portfolio
          </Typography>
        </Box>
        
        {canEdit && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            fullWidth={isMobile}
            size="large"
            sx={{ borderRadius: 3, px: 4 }}
          >
            New Project
          </Button>
        )}
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : (
        <>
          {isMobile ? (
            <Grid container spacing={2}>
              {projects.map((project) => (
                <Grid {...{ item: true, xs: 12 }} key={project.id}>
                  <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{project.name}</Typography>
                        <Chip 
                          label={project.status} 
                          color={getStatusColor(project.status) as any}
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: 1.5 }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.6 }}>
                        {project.description}
                      </Typography>
                      <Stack direction="row" spacing={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon fontSize="small" sx={{ color: 'primary.main' }} />
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {new Date(project.start_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PriorityIcon fontSize="small" color={getPriorityColor(project.priority) as any} />
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{project.priority}</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                    <Divider />
                    <CardActions sx={{ justifyContent: 'flex-end', p: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                      <Button size="small" variant="text" startIcon={<ViewIcon />} onClick={() => navigate(`/projects/${project.id}`)} sx={{ fontWeight: 700 }}>View</Button>
                      {canEdit && (
                        <Tooltip title="Add Task">
                          <IconButton size="small" color="primary" onClick={() => handleOpenTaskDialog(project)}><TaskIcon /></IconButton>
                        </Tooltip>
                      )}
                      {canEdit && <Button size="small" variant="text" startIcon={<EditIcon />} onClick={() => handleOpenDialog(project)} sx={{ fontWeight: 700 }}>Edit</Button>}
                      {canDelete && <IconButton size="small" color="error" onClick={() => { setProjectToDelete(project.id!); setDeleteDialogOpen(true); }}><DeleteIcon /></IconButton>}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.background.default, 0.8) }}>
                    <TableCell sx={{ pl: 4, py: 2.5, fontWeight: 800 }}>PROJECT NAME</TableCell>
                    <TableCell sx={{ py: 2.5, fontWeight: 800 }}>STATUS</TableCell>
                    <TableCell sx={{ py: 2.5, fontWeight: 800 }}>PRIORITY</TableCell>
                    <TableCell sx={{ py: 2.5, fontWeight: 800 }}>TIMELINE</TableCell>
                    <TableCell sx={{ py: 2.5, fontWeight: 800 }}>BUDGET</TableCell>
                    <TableCell align="right" sx={{ pr: 4, py: 2.5, fontWeight: 800 }}>ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projects.map((project) => (
                    <TableRow key={project.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ pl: 4, py: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                          <Avatar sx={{ width: 44, height: 44, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 800, fontSize: '1.2rem' }}>
                            {project.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{project.name}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {project.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={project.status} 
                          color={getStatusColor(project.status) as any}
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: 1.5, px: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={project.priority} 
                          color={getPriorityColor(project.priority) as any}
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: 1.5, px: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(project.start_date).toLocaleDateString()}</Typography>
                        <Typography variant="caption" color="text.secondary">to {new Date(project.target_end_date).toLocaleDateString()}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {project.budget ? `$${project.budget.toLocaleString()}` : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 4 }}>
                        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => navigate(`/projects/${project.id}`)} color="primary" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canEdit && (
                            <Tooltip title="Quick Add Task">
                              <IconButton size="small" onClick={() => handleOpenTaskDialog(project)} color="success" sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canEdit && (
                            <Tooltip title="Edit Project">
                              <IconButton size="small" onClick={() => handleOpenDialog(project)} color="info" sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete Project">
                              <IconButton size="small" onClick={() => { setProjectToDelete(project.id!); setDeleteDialogOpen(true); }} color="error" sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {projects.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 10, bgcolor: 'background.paper', borderRadius: 4, border: '2px dashed', borderColor: 'divider' }}>
              <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>No projects found</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>Start by creating your first Zandrigo project</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={{ borderRadius: 2, px: 4 }}>Create Project</Button>
            </Box>
          )}
        </>
      )}

      {/* Project Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1, fontSize: '1.5rem' }}>{isEditing ? 'Edit Project' : 'Create New Project'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField label="Project Name" name="name" fullWidth value={currentProject.name || ''} onChange={handleInputChange} required variant="outlined" InputProps={{ sx: { borderRadius: 2 } }} />
            <TextField label="Description" name="description" fullWidth multiline rows={4} value={currentProject.description || ''} onChange={handleInputChange} variant="outlined" InputProps={{ sx: { borderRadius: 2 } }} />
            <Grid container spacing={2}>
              <Grid {...{ item: true, xs: 6 }}>
                <TextField label="Start Date" name="start_date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={currentProject.start_date || ''} onChange={handleInputChange} required InputProps={{ sx: { borderRadius: 2 } }} />
              </Grid>
              <Grid {...{ item: true, xs: 6 }}>
                <TextField label="Target End Date" name="target_end_date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={currentProject.target_end_date || ''} onChange={handleInputChange} required InputProps={{ sx: { borderRadius: 2 } }} />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid {...{ item: true, xs: 6 }}>
                <TextField label="Status" name="status" select fullWidth value={currentProject.status || 'Not Started'} onChange={handleInputChange} required InputProps={{ sx: { borderRadius: 2 } }}>
                  {['Not Started', 'In Progress', 'On Hold', 'Completed', 'Cancelled'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid {...{ item: true, xs: 6 }}>
                <TextField label="Priority" name="priority" select fullWidth value={currentProject.priority || 'Medium'} onChange={handleInputChange} required InputProps={{ sx: { borderRadius: 2 } }}>
                  {['Low', 'Medium', 'High'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <TextField label="Budget ($)" name="budget" type="number" fullWidth value={currentProject.budget || ''} onChange={handleInputChange} InputProps={{ sx: { borderRadius: 2 } }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 4, pt: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" size="large" sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Quick Task Dialog */}
      <Dialog open={openTaskDialog} onClose={() => setOpenTaskDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Quick Add Task: {taskProject?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField label="Task Title" fullWidth value={newTask.title} onChange={(e) => setNewTask({...newTask, title: e.target.value})} required />
            <TextField label="Description" fullWidth multiline rows={2} value={newTask.description} onChange={(e) => setNewTask({...newTask, description: e.target.value})} />
            <TextField label="Due Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={newTask.due_date} onChange={(e) => setNewTask({...newTask, due_date: e.target.value})} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 4 }}>
          <Button onClick={() => setOpenTaskDialog(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button onClick={handleTaskSubmit} variant="contained" sx={{ borderRadius: 2, px: 3, fontWeight: 800 }}>Add Task</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography sx={{ lineHeight: 1.6 }}>Are you sure you want to delete <strong>{projects.find(p => p.id === projectToDelete)?.name}</strong>? This action is irreversible and all associated tasks will be removed.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 4 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}>Delete Project</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectsPage;